import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, TimePicker, Typography, Space, Progress } from 'antd';
import { ExclamationCircleOutlined, CheckOutlined, ClockCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDebounce } from 'react-use';

const { Title, Text, Paragraph } = Typography;



function durationToMs(duration) {
    if (!duration || typeof duration !== "string") return 0;

    let hoursMatch = duration.match(/(\d+)h/);
    let minsMatch = duration.match(/(\d+)m/);

    let hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    let minutes = minsMatch ? parseInt(minsMatch[1], 10) : 0;

    return (hours * 60 * 60 * 1000) + (minutes * 60 * 1000); // ms
}

function calculateTotalWork() {
    let totalMs = 0;

    try {
        // 1. Get and validate dailyTask array
        const storedTasks = localStorage.getItem("dailyTask");
        const dailyTask = storedTasks == 'null' ? [] : JSON.parse(storedTasks);
        if (!Array.isArray(dailyTask)) {
            console.error('dailyTask is not an array:', dailyTask);
            return { totalMs: 0, formatted: '0h 0m', isEightHoursDone: false };
        }
        // 2. Process completed tasks
        dailyTask.forEach((task, index) => {
            if (!task) {
                console.warn(`Skipping null/undefined task at index ${index}`);
                return;
            }
            // Handle manual duration tasks
            if (task.isManual === true && task.duration) {
                const duration = parseDuration(task.duration);
                if (duration > 0) {
                    totalMs += duration;
                }
                return;
            }
              
            // Handle timed tasks (must have both startTime and endTime)
            if (!task.isActive) {
                totalMs += durationToMs(task.duration);
                return;
            }
             
         
        });

        // 3. Add currently running task time
        const runningTasksStr = localStorage.getItem("runningTasks");
        if (runningTasksStr) {
            const runningTasks = JSON.parse(runningTasksStr);

            if (Array.isArray(runningTasks) && runningTasks.length > 0) {
                const currentIndex = runningTasks[0];

                if (typeof currentIndex === 'number') {
                    const taskKey = `task${currentIndex}StartTime`;
                    const taskStartTime = localStorage.getItem(taskKey);

                    if (taskStartTime) {
                        const start = dayjs(taskStartTime);

                        if (start.isValid()) {
                            let diff = dayjs().diff(start, "millisecond");
                            
                            if (diff > 0) {
                                let miliDuration = durationToMs(dailyTask[runningTasks]?.duration)
                                diff += miliDuration
                                totalMs += diff;
                            } else {
                                console.error('Running task has invalid time:', taskStartTime);
                            }
                        }
                    }
                }
            }
        }

    } catch (error) {
        console.error('Error in calculateTotalWork:', error);
        return { totalMs: 0, formatted: '0h 0m', isEightHoursDone: false };
    }

    // 4. Format output
    const hours = Math.floor(totalMs / (1000 * 60 * 60));
    const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
    const isEightHoursDone = totalMs >= 8 * 60 * 60 * 1000;
    // const isEightHoursDone = totalMs >= 1 * 60 * 1000;
    // console.log('Work calculation:', {
    //     totalMs,
    //     totalHours: (totalMs / (1000 * 60 * 60)).toFixed(2),
    //     formatted: `${hours}h ${minutes}m`,
    //     isEightHoursDone
    // });
          
    return {
        totalMs,
        formatted: `${hours}h ${minutes}m`,
        isEightHoursDone
    };
}

function parseDuration(durationStr) {
    if (!durationStr || typeof durationStr !== 'string') {
        return 0;
    }

    const match = durationStr.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);

    if (!match || (!match[1] && !match[2])) {
        console.warn('Could not parse duration:', durationStr);
        return 0;
    }

    const hours = parseInt(match[1] || "0", 10);
    const minutes = parseInt(match[2] || "0", 10);

    // Sanity check
    if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59) {
        console.error('Invalid duration values:', { hours, minutes, original: durationStr });
        return 0;
    }

    // return (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);
    return (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);
  
}
const ShiftManagementApp = ({ setOverTime, markTimeOut, timeIn }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [overtimeEndModalVisible, setOvertimeEndModalVisible] = useState(false);
    const [countdown, setCountdown] = useState(60); // Default autoLogoutTime
    const [overtimeCountdown, setOvertimeCountdown] = useState(60); // Countdown for overtime end modal
    const [showOvertimeForm, setShowOvertimeForm] = useState(false);
    const [overtimeEndTime, setOvertimeEndTime] = useState(null);
    const [validationError, setValidationError] = useState('');
    const [liveOvertimeClock, setLiveOvertimeClock] = useState('00:00:00');
    const [soundPlaying, setSoundPlaying] = useState(false);
    const currentAdmin = useSelector(selectCurrentAdmin);
    const [workStatus, setWorkStatus] = useState(calculateTotalWork());

    // localStorage keys
    const OVERTIME_HISTORY_KEY = 'shift_overtime_history';
    const CURRENT_OVERTIME_SESSION_KEY = 'shift_current_overtime_session';

    useEffect(() => {
        const interval = setInterval(() => {
            setWorkStatus(calculateTotalWork());
        }, 1000); // update every second (or every 60s for efficiency)

        return () => clearInterval(interval);
    }, []);

    // Initialize state from localStorage
    const [overtimeHistory, setOvertimeHistory] = useState(() => {
        try {
            const saved = localStorage.getItem(OVERTIME_HISTORY_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch (error) {
            console.error('Error loading overtime history from localStorage:', error);
            return [];
        }
    });

    const [currentOvertimeSession, setCurrentOvertimeSession] = useState(() => {
        try {
            const saved = localStorage.getItem(CURRENT_OVERTIME_SESSION_KEY);
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            console.error('Error loading current overtime session from localStorage:', error);
            return null;
        }
    });

    const audioRef = useRef(null);
    const intervalRef = useRef(null);

    const shiftEndTime = workStatus.isEightHoursDone;

    // Save to localStorage whenever overtime data changes
    useEffect(() => {
        try {
            localStorage.setItem(OVERTIME_HISTORY_KEY, JSON.stringify(overtimeHistory));
        } catch (error) {
            console.error('Error saving overtime history to localStorage:', error);
        }
    }, [overtimeHistory]);

    useEffect(() => {
        try {
            if (currentOvertimeSession) {
                localStorage.setItem(CURRENT_OVERTIME_SESSION_KEY, JSON.stringify(currentOvertimeSession));
            } else {
                localStorage.removeItem(CURRENT_OVERTIME_SESSION_KEY);
            }
        } catch (error) {
            console.error('Error saving current overtime session to localStorage:', error);
        }
    }, [currentOvertimeSession]);

    const playNotificationSound = () => {
        audioRef.current = new Audio("/mixkit-happy-bells-notification-937.wav");
        audioRef.current.loop = false; // manual loop control

        const startTime = Date.now();

        const playOnce = () => {
            if (!audioRef.current) return;

            audioRef.current.currentTime = 0;
            audioRef.current
                .play()
                .catch((err) => console.error("Audio play failed:", err));

            // auto-stop after 60s
            if (Date.now() - startTime > 60000) {
                stopNotificationSound();
            }
        };
           
        // play immediately
        playOnce(); 
        // repeat every 3s
        intervalRef.current = setInterval(playOnce, 3000);
    };

    const stopNotificationSound = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
        }
    };

    // Calculate total overtime duration in real-time
    const calculateTotalOvertimeDuration = () => {
        let totalSeconds = 0;

        // Add completed sessions
        overtimeHistory.forEach(session => {
            if (session.startTime && session.endTime) {
                const start = dayjs(session.startTime);
                const end = dayjs(session.endTime);
                totalSeconds += end.diff(start, 'seconds');
            }
        });

        // Add current active session if exists
        if (currentOvertimeSession && currentOvertimeSession.startTime) {
            const start = dayjs(currentOvertimeSession.startTime);
            const now = dayjs();
            totalSeconds += now.diff(start, 'seconds');
        }

        return totalSeconds;
    };

    // Format seconds to HH:MM:SS format
    const formatOvertimeClock = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Update live overtime clock every second
    useEffect(() => {
        let clockInterval;

        // Only run clock if there's an active overtime session or completed sessions
        if (currentOvertimeSession || overtimeHistory.length > 0) {
            clockInterval = setInterval(() => {
                const totalSeconds = calculateTotalOvertimeDuration();
                setLiveOvertimeClock(formatOvertimeClock(totalSeconds));
            }, 1000);
        } else {
            setLiveOvertimeClock('00:00:00');
        }

        return () => {
            if (clockInterval) clearInterval(clockInterval);
        };
    }, [currentOvertimeSession, overtimeHistory]);

    // Effect to automatically open shift end modal when shiftEndTime passes
    useEffect(() => {
        const checkShiftEnd = () => {
            const now = dayjs();  // ✅ this is a dayjs object
            // Only show shift end modal if:
            // 1. Shift time has passed
            // 2. No modals are currently visible
            // 3. No active overtime session (overtime takes precedence)
            if (shiftEndTime && !modalVisible && !overtimeEndModalVisible && !currentOvertimeSession) {
                if (!soundPlaying) { // Only start sound if not already playing
                    playNotificationSound();
                }
                setModalVisible(true);
            }
        };

        // Initial check
        checkShiftEnd();

        // Set interval to check every second
        const interval = setInterval(checkShiftEnd, 1000);

        return () => clearInterval(interval);
    }, [modalVisible, overtimeEndModalVisible, currentOvertimeSession, soundPlaying, shiftEndTime]);

    // Effect to automatically open overtime end modal when overtime ends
    useEffect(() => {
        if (!currentOvertimeSession || !currentOvertimeSession.plannedEndTime) return;

        const checkOvertimeEnd = () => {
            const now = dayjs();
            const plannedEnd = dayjs(currentOvertimeSession.plannedEndTime);

            if (plannedEnd.isBefore(now) && !overtimeEndModalVisible && !modalVisible) {
                if (!soundPlaying) { // Only start sound if not already playing
                    playNotificationSound();
                }
                setOvertimeEndModalVisible(true);
            }
        };

        // Initial check
        checkOvertimeEnd();

        // Set interval to check every second
        const interval = setInterval(checkOvertimeEnd, 1000);

        return () => clearInterval(interval);
    }, [currentOvertimeSession, overtimeEndModalVisible, modalVisible, soundPlaying]);

    // Countdown timer effect for shift end modal
    useEffect(() => {
        let timer;
        if (modalVisible && countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        handleEndShift();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [modalVisible, countdown]);

    // Countdown timer effect for overtime end modal
    useEffect(() => {
        let timer;
        if (overtimeEndModalVisible && overtimeCountdown > 0) {
            timer = setInterval(() => {
                setOvertimeCountdown(prev => {
                    if (prev <= 1) {
                        handleEndShift();
                        setOvertimeEndModalVisible(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [overtimeEndModalVisible, overtimeCountdown]);

    // Reset countdown and form when shift end modal opens
    useEffect(() => {
        if (modalVisible) {
            setCountdown(60); // Reset to autoLogoutTime
            setShowOvertimeForm(false);
            setOvertimeEndTime(null);
            setValidationError('');
        }
    }, [modalVisible]);

    // Reset countdown when overtime end modal opens
    useEffect(() => {
        if (overtimeEndModalVisible) {
            setOvertimeCountdown(60); // Reset to autoLogoutTime
        }
    }, [overtimeEndModalVisible]);

    // Add cleanup effect to stop sound when component unmounts
    useEffect(() => {
        return () => {
            stopNotificationSound();
        };
    }, []);

    const handleEndShift = () => {
        stopNotificationSound(); // Stop sound when user takes action

        // If there's an active overtime session, mark it as ended
        if (currentOvertimeSession) {
            const endedSession = {
                ...currentOvertimeSession,
                actualEndTime: dayjs().toISOString(),
                endTime: dayjs().toISOString()
            };
            setOvertimeHistory(prev => {
                const updated = [...prev, endedSession];
                try {
                    localStorage.setItem(OVERTIME_HISTORY_KEY, JSON.stringify(updated));
                } catch (error) {
                    console.error('Error saving overtime history to localStorage:', error);
                }
                return updated;
            });
            setCurrentOvertimeSession(null);
            try {
                localStorage.removeItem(CURRENT_OVERTIME_SESSION_KEY);
            } catch (error) {
                console.error('Error removing current overtime session from localStorage:', error);
            }
        }

        setOverTime('00:00:00')
        setModalVisible(false);
        setOvertimeEndModalVisible(false);
        markTimeOut(liveOvertimeClock || '00:00:00');
    };

    const handleStartOvertime = () => {
        if (!showOvertimeForm) {
            setShowOvertimeForm(true);
            return;
        }

        stopNotificationSound(); // Stop sound when user starts overtime

        if (!overtimeEndTime) {
            setValidationError('Please select overtime end time!');
            return;
        }

        // Format for setOverTime (12-hour, h:mm A)
        const formattedTime12 = overtimeEndTime.format('h:mm A');

        // Validation against current time - overtime must be in the future
        const now = dayjs();
        const selectedDateTime = dayjs().hour(overtimeEndTime.hour()).minute(overtimeEndTime.minute()).second(0);

        // If selected time is before current time, assume it's for tomorrow
        let finalOvertimeTime = selectedDateTime;
        if (selectedDateTime.isBefore(now)) {
            finalOvertimeTime = selectedDateTime.add(1, 'day');
        }

        // Must be at least 1 minute in the future
        if (finalOvertimeTime.diff(now, 'minutes') < 1) {
            setValidationError('Overtime end time must be at least 1 minute in the future!');
            return;
        }

        setValidationError('');

        // If there's an existing overtime session, end it first
        if (currentOvertimeSession) {
            const endedSession = {
                ...currentOvertimeSession,
                actualEndTime: dayjs().toISOString(),
                endTime: dayjs().toISOString()
            };
            setOvertimeHistory(prev => {
                const updated = [...prev, endedSession];
                try {
                    localStorage.setItem(OVERTIME_HISTORY_KEY, JSON.stringify(updated));
                } catch (error) {
                    console.error('Error saving overtime history to localStorage:', error);
                }
                return updated;
            });
        }

        // Create new overtime session with proper datetime
        const newOvertimeSession = {
            id: Date.now(),
            startTime: dayjs().toISOString(),
            plannedEndTime: finalOvertimeTime.toISOString(),
            sessionNumber: overtimeHistory.length + (currentOvertimeSession ? 1 : 0) + 1
        };

        setCurrentOvertimeSession(newOvertimeSession);
        try {
            localStorage.setItem(CURRENT_OVERTIME_SESSION_KEY, JSON.stringify(newOvertimeSession));
        } catch (error) {
            console.error('Error saving current overtime session to localStorage:', error);
        }

        setOverTime(formattedTime12); // Passes 12-hour format
        setModalVisible(false);

        // Clear overtime end time as we're now using plannedEndTime from session
        stopNotificationSound();
        handleOverTimeSalckNot(formattedTime12)
        setOvertimeEndTime(null);
    };

    const handleOverTimeSalckNot = (overtimeEndTime) => {
        try {
            // Parse overtimeEndTime (assuming it's like "7:04 PM")
            const end = dayjs(overtimeEndTime, ["h:mm A"]);
            const now = dayjs();
            // If overtime time is for today but already passed, move it to tomorrow
            if (end.isBefore(now)) {
                end.add(1, "day");
            }

            // Difference in minutes
            const diffMinutes = end.diff(now, "minute");
            // Convert to hours (with 2 decimals)
            const diffHours = (diffMinutes / 60).toFixed(2);

            // Send to Slack
            axios.post("/slackOverTimeAlert", {
                name: `${currentAdmin.name} ${currentAdmin.surname}`,
                overtime: Number(diffHours), // e.g. "0.50h"
            });

        } catch (error) {
            console.error("error", error?.message);
        }
    };

    const handleExtendOvertime = () => {
        // Close overtime end modal and reopen shift end modal for extension
        setOvertimeEndModalVisible(false);
        setModalVisible(true);
        setShowOvertimeForm(false); // Reset form state
        setOvertimeEndTime(null);
        setValidationError('');
    };

    const handleCancel = () => {
        setShowOvertimeForm(false);
        setOvertimeEndTime(null);
        setValidationError('');
    };

    const handleModalClose = () => {
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercent = ((60 - countdown) / 60) * 100;
    const overtimeProgressPercent = ((60 - overtimeCountdown) / 60) * 100;

    const getCurrentTime = () => {
        return dayjs().format('h:mm:ss A'); // 12-hour format with AM/PM
    };

    const formatShiftTime = (timeStr) => {
        if (!timeStr) return 'N/A';
        if (typeof timeStr === 'string') return timeStr;
        return dayjs(timeStr).format('h:mm A'); // 12-hour format with AM/PM
    };

    return (
        <>
            {/* Shift End Modal */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <span>⚠️ Shift Ended – Choose Next Action</span>
                    </Space>
                }
                open={modalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={500}
                centered
                maskClosable={false}
                closable={false}
            >
                <div style={{ padding: '16px 0' }}>
                    {/* User Info Section */}
                    <div style={{
                        background: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '20px'
                    }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>Employee: {`${currentAdmin.name} ${currentAdmin.surname}` || 'Unknown'}</Text>
                            {/* <Text type="secondary">ID: {currentAdmin._id || 'N/A'}</Text> */}
                            {/* <Text type="secondary">Department: {currentAdmin.role || 'N/A'}</Text> */}
                            <Text type="secondary">
                                Shift End Time: {formatShiftTime(shiftEndTime)}
                            </Text>
                            <Text type="secondary">
                                Current Time: {getCurrentTime()}
                            </Text>
                            {currentOvertimeSession && (
                                <Text type="secondary" style={{ color: '#1890ff' }}>
                                    Active Overtime Session #{currentOvertimeSession.sessionNumber} - Ends at: {dayjs(currentOvertimeSession.plannedEndTime).format('h:mm A')}
                                </Text>
                            )}
                            {(overtimeHistory.length > 0 || currentOvertimeSession) && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '8px 12px',
                                    background: '#e6f7ff',
                                    borderRadius: '4px',
                                    border: '1px solid #91d5ff'
                                }}>
                                    <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                        ⏱️ Total Overtime: {liveOvertimeClock}
                                    </Text>
                                </div>
                            )}
                        </Space>
                    </div>

                    {/* Main Message */}
                    <Paragraph style={{ fontSize: '16px', marginBottom: '20px' }}>
                        Your scheduled shift has ended. Please select one of the following options to proceed:
                    </Paragraph>

                    <div style={{ marginBottom: '20px' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>• <strong>End Shift</strong> – Marks your time-out and logs you out of the system.</Text>
                            <Text>• <strong>Start Overtime</strong> – Continue working beyond your scheduled shift. You'll be asked to enter your overtime end time, and the system will remind you before your overtime ends.</Text>
                        </Space>
                    </div>

                    {/* Overtime Form */}
                    {showOvertimeForm && (
                        <div style={{
                            background: '#e6f7ff',
                            padding: '16px',
                            borderRadius: '6px',
                            marginBottom: '20px',
                            border: '1px solid #91d5ff'
                        }}>
                            <Title level={5} style={{ marginBottom: '12px' }}>
                                <ClockCircleOutlined /> Set Overtime End Time
                                {overtimeHistory.length > 0 && (
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '8px' }}>
                                        (Session #{overtimeHistory.length + (currentOvertimeSession ? 1 : 0) + 1})
                                    </Text>
                                )}
                            </Title>
                            <div style={{ marginBottom: '12px' }}>
                                <Text style={{ marginBottom: '8px', display: 'block' }}>
                                    Expected Overtime End Time *
                                </Text>
                                <TimePicker
                                    value={overtimeEndTime}
                                    onChange={(time) => {
                                        setOvertimeEndTime(time);
                                        setValidationError('');
                                    }}
                                    format="h:mm A" // 12-hour format with AM/PM
                                    use12Hours
                                    minuteStep={15} // Add this line
                                    placeholder="Select end time"
                                    style={{ width: '100%' }}
                                    showNow={false}
                                />
                                {validationError && (
                                    <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                        {validationError}
                                    </Text>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Auto-logout Warning */}
                    <div style={{
                        background: '#fff2e8',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        border: '1px solid #ffbb96'
                    }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong style={{ color: '#fa541c' }}>
                                ⏰ Auto-logout Warning
                            </Text>
                            <Text>
                                If no action is taken, you will be automatically logged out in{' '}
                                <strong style={{ color: '#fa541c' }}>{formatTime(countdown)}</strong>
                            </Text>
                            <Progress
                                percent={progressPercent}
                                size="small"
                                strokeColor="#fa541c"
                                showInfo={false}
                            />
                        </Space>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CheckOutlined />}
                            onClick={handleEndShift}
                            style={{
                                background: '#52c41a',
                                borderColor: '#52c41a',
                                minWidth: '140px'
                            }}
                        >
                            ✅ End Shift
                        </Button>

                        <Button
                            type="primary"
                            size="large"
                            icon={<ClockCircleOutlined />}
                            onClick={handleStartOvertime}
                            style={{
                                background: '#1890ff',
                                borderColor: '#1890ff',
                                minWidth: '140px'
                            }}
                        >
                            ⏱️ {showOvertimeForm ? 'Confirm Overtime' : 'Start Overtime'}
                        </Button>

                        {showOvertimeForm && (
                            <Button
                                size="large"
                                icon={<CloseOutlined />}
                                onClick={handleCancel}
                                style={{ minWidth: '100px' }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Overtime End Modal */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <span>⚠️ Overtime Ended – Choose Next Action</span>
                    </Space>
                }
                open={overtimeEndModalVisible}
                footer={null}
                width={500}
                centered
                maskClosable={false}
                closable={false}
            >
                <div style={{ padding: '16px 0' }}>
                    {/* User Info Section */}
                    <div style={{
                        background: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '20px'
                    }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>Employee: {`${currentAdmin.name} ${currentAdmin.surname}` || 'Unknown'}</Text>
                            {/* <Text type="secondary">ID: {currentAdmin._id || 'N/A'}</Text> */}
                            {/* <Text type="secondary">Department: {currentAdmin.role || 'N/A'}</Text> */}
                            <Text type="secondary">
                                Overtime End Time: {currentOvertimeSession ? dayjs(currentOvertimeSession.plannedEndTime).format('h:mm A') : 'N/A'}
                            </Text>
                            <Text type="secondary">
                                Current Time: {getCurrentTime()}
                            </Text>
                            <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                background: '#e6f7ff',
                                borderRadius: '4px',
                                border: '1px solid #91d5ff'
                            }}>
                                <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                    ⏱️ Total Overtime: {liveOvertimeClock}
                                </Text>
                            </div>
                            {currentOvertimeSession && (
                                <Text type="secondary">
                                    Current Session: #{currentOvertimeSession.sessionNumber}
                                </Text>
                        )}
                        </Space>
                    </div>

                    {/* Main Message */}
                    <Paragraph style={{ fontSize: '16px', marginBottom: '20px' }}>
                        Your overtime has ended. Please select one of the following options to proceed:
                    </Paragraph>

                    <div style={{ marginBottom: '20px' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>• <strong>End Shift</strong> – Marks your time-out and logs you out of the system.</Text>
                            <Text>• <strong>Extend Overtime</strong> – Continue working beyond your current overtime. You'll be asked to enter a new overtime end time.</Text>
                        </Space>
                    </div>

                    {/* Auto-logout Warning for Overtime End */}
                    <div style={{
                        background: '#fff2e8',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        border: '1px solid #ffbb96'
                    }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong style={{ color: '#fa541c' }}>
                                ⏰ Auto-logout Warning
                            </Text>
                            <Text>
                                If no action is taken, you will be automatically logged out in{' '}
                                <strong style={{ color: '#fa541c' }}>{formatTime(overtimeCountdown)}</strong>
                            </Text>
                            <Progress
                                percent={overtimeProgressPercent}
                                size="small"
                                strokeColor="#fa541c"
                                showInfo={false}
                            />
                        </Space>
                    </div>

                    {/* Action Buttons for Overtime End */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CheckOutlined />}
                            onClick={() => {
                                handleEndShift();
                                setOvertimeEndModalVisible(false);
                            }}
                            style={{
                                background: '#52c41a',
                                borderColor: '#52c41a',
                                minWidth: '140px'
                            }}
                        >
                            ✅ End Shift
                        </Button>

                        <Button
                            type="primary"
                            size="large"
                            icon={<ClockCircleOutlined />}
                            onClick={handleExtendOvertime}
                            style={{
                                background: '#1890ff',
                                borderColor: '#1890ff',
                                minWidth: '140px'
                            }}
                        >
                            ⏱️ Extend Overtime
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ShiftManagementApp;
